import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Divider,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '@mui/material/styles';

import { db, storage } from '../../firebase';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject, uploadBytes, getDownloadURL } from 'firebase/storage';
import { AuthContext } from '../../contexts/AuthContext';
import { addLog } from '../../services/logs';

// Import the new components
import ProductImageGallery from './components/ProductImageGallery';
import ProductBasicInfo from './components/ProductBasicInfo';
import ImageDialog from './components/ImageDialog';
import DeleteDialog from './components/DeleteDialog';
import ProductDetailsInfo from './components/ProductDetailsInfo';

const ProductDetails = ({ productId, product: productFromProps, open, onClose, readOnly = false }) => {
  const theme = useTheme();
  const { currentUser, isAdmin, isMarketing } = useContext(AuthContext);
  const navigate = useNavigate();

  // State Management
  const [product, setProduct] = useState(null);
  const [editedProduct, setEditedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [editMode, setEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Image Gallery State
  const [images, setImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  // Dialogs State
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  
  // Snackbar State
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Data Fetching
  const fetchProduct = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    setError(null);
    try {
      const productRef = doc(db, 'products', productId);
      const productSnap = await getDoc(productRef);

      if (productSnap.exists()) {
        const productData = { id: productSnap.id, ...productSnap.data() };
        setProduct(productData);
        setEditedProduct(productData);

        // Backward compatibility: handle both `images` and `imageUrls` fields, prioritizing 'images'
        const rawImagesData = productData.images || productData.imageUrls || [];
        const formattedImages = rawImagesData.map(img => 
          (typeof img === 'string') ? { url: img, name: '' } : img
        );
        setImages(formattedImages);
      } else {
        setError('Nie znaleziono produktu.');
        console.error("No such document!");
      }
    } catch (err) {
      setError('Błąd podczas pobierania danych produktu.');
      console.error("Error fetching document:", err);
    } finally {
      setLoading(false);
    }
  }, [productId]);

    useEffect(() => {
    if (open) {
      if (productFromProps) {
        // Jeśli produkt jest przekazany jako prop, użyj go bezpośrednio
        const productData = { id: productFromProps.id, ...productFromProps };
        setProduct(productData);
        setEditedProduct(productData);
        const rawImagesData = productData.images || productData.imageUrls || [];
        const formattedImages = rawImagesData.map(img => 
          (typeof img === 'string') ? { url: img, name: '' } : img
        );
        setImages(formattedImages);
        setLoading(false);
        setError(null);
      } else if (productId) {
        // W przeciwnym razie, pobierz produkt z Firestore
        fetchProduct();
      }
    }
  }, [productFromProps, productId, open, fetchProduct]);

  // Handlers
  const handleSaveChanges = async () => {
    setIsSubmitting(true);
    try {
      const productRef = doc(db, 'products', productId);
      
      // Prepare the data to update, including the new image order
      // Zapisujemy zarówno w imageUrls jak i images, aby zachować kompatybilność
      // i zapewnić poprawne wyświetlanie w ProductsList
      const updatedData = {
        ...editedProduct,
        imageUrls: images,
        images: images.map(img => img.url) // Zapisz tylko URLe w images dla zgodności z ProductsList
      };

      await updateDoc(productRef, updatedData);
      await addLog({
      action: 'Update Product',
      details: `Zaktualizowano produkt SKU: ${editedProduct.sku || productId}`,
      entity_type: 'product',
      entity_id: productId,
      user_id: currentUser.uid
    });
      
      setSnackbar({ open: true, message: 'Zmiany zostały zapisane pomyślnie.', severity: 'success' });
      setEditMode(false);
      fetchProduct(); // Refresh data to show the saved state
    } catch (error) {
      console.error('Error saving changes:', error);
      setSnackbar({ open: true, message: `Błąd podczas zapisywania zmian: ${error.message}`, severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProduct = async () => {
    setIsSubmitting(true);
    try {
      // Delete images from Storage first
      if (product.imageUrls && product.imageUrls.length > 0) {
        for (const imageUrl of product.imageUrls) {
          try {
            const imageRef = ref(storage, imageUrl.url);
            await deleteObject(imageRef);
          } catch (storageError) {
            if (storageError.code === 'storage/object-not-found') {
              console.warn(`Image not found, continuing deletion: ${imageUrl.url}`);
            } else {
              throw storageError;
            }
          }
        }
      }

      // Delete the product document from Firestore
      await deleteDoc(doc(db, 'products', productId));
      await addLog({
        action: 'Delete Product',
        details: `Usunięto produkt SKU: ${product.sku || productId}`,
        entity_type: 'product',
        entity_id: productId,
        user_id: currentUser.uid
      });

      setSnackbar({ open: true, message: 'Produkt został usunięty.', severity: 'success' });
      setOpenDeleteDialog(false);
      onClose(); // Close the main dialog
    } catch (error) {
      console.error('Error deleting product:', error);
      setSnackbar({ open: true, message: `Błąd podczas usuwania produktu: ${error.message}`, severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageReorder = (reorderedImages) => {
    setImages(reorderedImages);
    setEditedProduct({ ...editedProduct, imageUrls: reorderedImages });
  };

  const handleAddImage = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const imageRef = ref(storage, `products/${productId}/${Date.now()}_${file.name}`);
        await uploadBytes(imageRef, file);
        const downloadURL = await getDownloadURL(imageRef);
        return { url: downloadURL, name: file.name };
      });

      const newImageUrls = await Promise.all(uploadPromises);
      const updatedImages = [...images, ...newImageUrls];
      
      setImages(updatedImages);
      setEditedProduct({ ...editedProduct, imageUrls: updatedImages });

      setSnackbar({ open: true, message: 'Zdjęcia zostały dodane.', severity: 'success' });
    } catch (error) {
      console.error("Error uploading images:", error);
      setSnackbar({ open: true, message: `Błąd podczas dodawania zdjęć: ${error.message}`, severity: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveExistingImage = async (indexToRemove) => {
    const imageToRemove = images[indexToRemove];
    
    // Optimistically update the UI
    const updatedImages = images.filter((_, index) => index !== indexToRemove);
    setImages(updatedImages);
    setEditedProduct({ ...editedProduct, imageUrls: updatedImages });

    try {
      // No need to delete from storage immediately if it's a new, unsaved image
      // The deletion from storage will happen on save or can be handled differently.
      // For now, we just remove from the state.
      setSnackbar({ open: true, message: 'Zdjęcie usunięte z listy. Zapisz zmiany, aby usunąć je na stałe.', severity: 'info' });
    } catch (error) {
      // Revert UI change if there was an error
      setImages(images);
      setSnackbar({ open: true, message: `Błąd podczas usuwania zdjęcia: ${error.message}`, severity: 'error' });
    }
  };


  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Błąd</DialogTitle>
        <DialogContent>
          <Alert severity="error">{error}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Zamknij</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{ sx: { height: '95vh', maxHeight: '95vh' } }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div">
          Szczegóły produktu: {product?.jewelry_name || product?.brand && product?.model ? `${product.brand} ${product.model}` : product?.sku || ''}
        </Typography>
        <Box>
          {editMode ? (
            <>
              <Tooltip title="Zapisz zmiany">
                <IconButton onClick={handleSaveChanges} disabled={isSubmitting}>
                  {isSubmitting ? <CircularProgress size={24} /> : <SaveIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Anuluj edycję">
                <IconButton onClick={() => setEditMode(false)}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            !readOnly && (isAdmin || isMarketing) && (
              <Tooltip title="Edytuj produkt">
                <IconButton onClick={() => setEditMode(true)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )
          )}
          {!readOnly && isAdmin && (
            <Tooltip title="Usuń produkt">
              <IconButton onClick={() => setOpenDeleteDialog(true)} disabled={isSubmitting}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <ProductImageGallery 
              images={images}
              onImagesReorder={handleImageReorder}
              selectedImageIndex={selectedImageIndex}
              setSelectedImageIndex={setSelectedImageIndex}
              editMode={editMode}
              handleAddImage={handleAddImage}
              isUploading={isUploading}
              handleImageClick={(index) => {
                setSelectedImageIndex(index);
                setOpenImageDialog(true);
              }}
              onRemoveImage={handleRemoveExistingImage}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <ProductBasicInfo 
              product={product}
              editedProduct={editedProduct}
              setEditedProduct={setEditedProduct}
              editMode={editMode}
            />
            <Divider sx={{ my: 2 }} />
            <ProductDetailsInfo 
              product={product}
              editedProduct={editedProduct}
              setEditedProduct={setEditedProduct}
              editMode={editMode}
            />
          </Grid>
        </Grid>

        <ImageDialog 
          open={openImageDialog}
          onClose={() => setOpenImageDialog(false)}
          images={images}
          selectedIndex={selectedImageIndex}
          onChangeImage={setSelectedImageIndex}
          product={product}
        />

        <DeleteDialog 
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onConfirm={handleDeleteProduct}
          productId={product?.id}
          isSubmitting={isSubmitting}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity} 
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </DialogContent>
    </Dialog>
  );
};

export default ProductDetails;
